import { format } from 'date-fns'

/**
 * Always allocates a fresh version for saves: today's `yyyyMMdd`, then `_v1`, `_v2`, …
 * when that base (or a prior suffix) already exists for the route.
 */
export function allocateRouteVersion(existingVersions: string[]): string {
  const baseVersion = format(new Date(), 'yyyyMMdd')
  const existing = new Set(existingVersions)
  let routeVersion = baseVersion
  for (let v = 1; existing.has(routeVersion); v++) {
    routeVersion = `${baseVersion}_v${v}`
  }
  return routeVersion
}

const VERSION_SUFFIX_RE = /^(.*)_v(\d+)$/

function parseVersion(version: string): { base: string; suffix: number } {
  const match = VERSION_SUFFIX_RE.exec(version)
  if (!match) return { base: version, suffix: 0 }
  return { base: match[1]!, suffix: Number(match[2]) }
}

/**
 * Newest-first comparator for `yyyyMMdd` / `yyyyMMdd_vN` versions. The `_vN` suffix is
 * compared numerically so `_v10` sorts newer than `_v9` (plain lexicographic ordering
 * breaks at ten same-day versions).
 */
export function compareRouteVersionsDesc(a: string, b: string): number {
  const left = parseVersion(a)
  const right = parseVersion(b)
  if (left.base !== right.base) return left.base > right.base ? -1 : 1
  return right.suffix - left.suffix
}

export function isLatestVersion(version: string, versionsForRoute: string[]): boolean {
  if (versionsForRoute.length === 0) return true
  const sorted = [...versionsForRoute].sort(compareRouteVersionsDesc)
  return sorted[0] === version
}
